import { InsightsNavbar } from "@/components/InsightsNavbar";
import { AnimatedSection } from "@/components/AnimatedSection";
import { ScrollProgressIndicator } from "@/components/ScrollProgressIndicator";
import { Footer } from "@/components/Footer";
import { InsightsLeadModal } from "@/components/InsightsLeadModal";
import { InsightsBreadcrumb } from "@/components/InsightsBreadcrumb";
import { InsightSEO } from "@/hooks/useInsightSEO";
import { RelatedTools } from "@/components/RelatedTools";
import { InsightEngagementTracker } from "@/components/InsightEngagementTracker";
import { InsightFAQSection } from "@/components/InsightFAQSection";
import { InsightBenchmarks } from "@/components/InsightBenchmarks";
import { TableOfContents } from "@/components/TableOfContents";
import { Badge } from "@/components/Badge";
import { StatPill } from "@/components/StatPill";
import {
  Bot,
  AlertTriangle,
  DollarSign,
  CalendarX,
  Building2,
  Lightbulb,
  ArrowRight,
  Cpu,
  LineChart,
  Wrench,
} from "lucide-react";
import amanImage from "@/assets/aman-sajid.png";

const HeroSection = () => (
  <section className="pt-8 sm:pt-12">
    <Badge>
      <Bot className="h-3 w-3 mr-1" />
      Enterprise AI Spend
    </Badge>

    <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mt-4 mb-3 leading-tight">
      Microsoft Drops Claude Code by June 30, 2026: Inside the AI Budget Blowout
    </h1>

    <p className="text-lg sm:text-xl text-muted-foreground mb-6 max-w-3xl">
      Six months after handing thousands of its own developers access to
      Anthropic's Claude Code, Microsoft is pulling the plug. The cutoff for
      its Experiences + Devices org — the engineers behind Windows, Microsoft
      365, Outlook, Teams and Surface — lines up exactly with its fiscal year
      end. Here are the verified facts, the sources, and what it tells us
      about per-token AI coding costs at scale.
    </p>

    <div className="flex flex-wrap gap-3 mb-8">
      <StatPill value="~100K" label="engineers affected (E+D)" />
      <StatPill value="Jun 30, 2026" label="Claude Code cutoff" />
      <StatPill value="6 months" label="rollout → rollback" />
      <StatPill value="Copilot CLI" label="forced replacement" />
    </div>

    <div className="bg-muted/30 border border-border rounded-xl p-6 sm:p-8">
      <h2
        id="overview"
        className="text-xl sm:text-2xl font-semibold text-foreground mb-4 flex items-center gap-2"
      >
        <CalendarX className="h-5 w-5 text-destructive" />
        What Microsoft told its engineers
      </h2>
      <div className="grid md:grid-cols-3 gap-6">
        <div>
          <p className="text-muted-foreground mb-4">
            <strong className="text-foreground">Who is affected</strong> —
            Microsoft's Experiences + Devices (E+D) organization, which
            includes the engineers responsible for Windows, Microsoft 365,
            Outlook, Microsoft Teams and Surface.
          </p>
          <ul className="space-y-2 text-muted-foreground">
            <li>
              <span className="text-primary font-semibold">~100,000</span>{" "}
              engineers (per ABP Live, Pomegra, byteiota; The Verge says
              "thousands")
            </li>
            <li>
              <span className="text-primary font-semibold">Dec 2025</span>{" "}
              rollout opened access to thousands of internal developers
            </li>
          </ul>
        </div>
        <div>
          <p className="text-muted-foreground mb-4">
            <strong className="text-foreground">When it ends</strong> —
            Engineers are being told to migrate to GitHub Copilot CLI in the
            weeks ahead, with the Claude Code shutoff timed to fiscal year
            end.
          </p>
          <ul className="space-y-2 text-muted-foreground">
            <li>
              <span className="text-primary font-semibold">Jun 30, 2026</span>{" "}
              — Microsoft fiscal year end (FY26)
            </li>
            <li>
              <span className="text-primary font-semibold">May 14, 2026</span>{" "}
              — Verge reporting; engineers notified in the same window
            </li>
          </ul>
        </div>
        <div>
          <p className="text-muted-foreground mb-4">
            <strong className="text-foreground">Why it ends</strong> — Two
            reasons, one official and one from sources speaking to The Verge.
          </p>
          <ul className="space-y-2 text-muted-foreground">
            <li>
              <span className="text-primary font-semibold">Official:</span>{" "}
              converge on GitHub Copilot CLI as the agentic CLI standard
            </li>
            <li>
              <span className="text-primary font-semibold">Unofficial:</span>{" "}
              financial — Claude Code's token-based spend ran past forecast
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
);

const Takeaway = () => (
  <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 sm:p-8">
    <div className="flex items-start gap-4">
      <div className="p-2 bg-primary/10 rounded-lg shrink-0">
        <Lightbulb className="h-6 w-6 text-primary" />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2">Key Takeaway</h3>
        <p className="text-muted-foreground mb-4">
          If Microsoft — the company whose Azure infrastructure literally hosts
          Anthropic and OpenAI workloads for the rest of the planet — cannot
          keep token-based Claude Code spend inside its own budget for six
          months, that is the loudest possible signal that per-token enterprise
          AI coding pricing does not scale linearly with adoption.
        </p>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-primary" />
            <span className="text-foreground font-medium">Per-token costs scale faster than headcount</span>
          </div>
          <div className="flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-primary" />
            <span className="text-foreground font-medium">"Unlimited" seats are more predictable than usage</span>
          </div>
          <div className="flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-primary" />
            <span className="text-foreground font-medium">Microsoft will prioritize its own Copilot economics</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const FounderQuote = () => (
  <div className="bg-card border border-border rounded-xl p-6 sm:p-8">
    <div className="flex flex-col sm:flex-row gap-6 items-start">
      <img
        src={amanImage}
        alt="Aman Sajid"
        className="w-16 h-16 rounded-full object-cover border-2 border-primary/20 shrink-0"
      />
      <div>
        <blockquote className="text-lg sm:text-xl text-foreground italic mb-4">
          "Microsoft gave roughly a hundred thousand of its own engineers
          Claude Code in December. Six months later it is taking it back,
          right on its fiscal year end, and telling them to use Copilot CLI.
          When the company that owns half of Anthropic's hosting can't
          stomach the token bill for its own developers, that is the entire
          enterprise AI cost story in one headline."
        </blockquote>

        <div className="bg-muted/40 border border-border rounded-lg p-4 mb-4">
          <p className="text-sm font-semibold text-foreground mb-3">
            Aman's read on the numbers:
          </p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>
                <strong className="text-foreground">The cutoff date is the tell.</strong>{" "}
                "End of June" is not a coincidence — it is Microsoft's fiscal year close (Jun 30). When a cancellation lines up perfectly with FY end, it is being booked, not debated.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>
                <strong className="text-foreground">Token billing punished success.</strong>{" "}
                The Verge says Claude Code became "very popular" inside Microsoft. In a per-token world, popularity is a cost, not a metric — and that is exactly what blew the budget.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>
                <strong className="text-foreground">Microsoft can't sell Copilot while its own engineers pick Claude.</strong>{" "}
                Internal preference for a competitor's tool is a sales problem before it is a finance problem. Convergence on Copilot CLI fixes both at once.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>
                <strong className="text-foreground">This is the same pattern as Uber.</strong>{" "}
                The Verge ran "Uber president says AI spending is getting harder to justify" the same week. Two of the largest enterprise AI buyers on Earth are saying the same thing in the same month.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>
                <strong className="text-foreground">What we tell IT Dukes clients:</strong>{" "}
                if you are paying per-token for a coding assistant across more than ~20 engineers, model the worst-case month at 3× last month — and put a hard per-seat cap before procurement does it for you in June.
              </span>
            </li>
          </ul>
        </div>

        <div>
          <p className="font-semibold text-foreground">Aman Sajid</p>
          <p className="text-sm text-muted-foreground">Founder, IT Dukes</p>
        </div>
      </div>
    </div>
  </div>
);

const headlineNumbers = [
  {
    icon: Building2,
    value: "Experiences + Devices",
    label: "Microsoft org losing Claude Code",
    sub: "Engineers for Windows, M365, Outlook, Teams, Surface.",
    color: "text-destructive",
  },
  {
    icon: CalendarX,
    value: "Jun 30, 2026",
    label: "Claude Code cutoff (FY26 end)",
    sub: "Cutoff aligns exactly with Microsoft's fiscal year end.",
    color: "text-amber-500",
  },
  {
    icon: Cpu,
    value: "GitHub Copilot CLI",
    label: "Forced replacement tool",
    sub: "Microsoft's own agentic CLI, command-line Copilot.",
    color: "text-blue-500",
  },
  {
    icon: AlertTriangle,
    value: "Dec 2025",
    label: "When Microsoft opened Claude Code access",
    sub: "~6 months between rollout and rollback (Verge, Notepad).",
    color: "text-orange-500",
  },
  {
    icon: DollarSign,
    value: "Token-based",
    label: "Billing model that broke the budget",
    sub: "Per-token Claude Code spend scaled past forecast (Verge sources).",
    color: "text-purple-500",
  },
  {
    icon: LineChart,
    value: "$30/user/mo",
    label: "Copilot Cowork (M365 E7) bundle price",
    sub: "Anthropic-powered tech, resold by Microsoft at fixed seat price (Stratechery, Mar 9, 2026).",
    color: "text-blue-500",
  },
];

const HeadlineNumbers = () => (
  <section className="py-10 sm:py-12">
    <h2
      id="headline-numbers"
      className="text-2xl sm:text-3xl font-bold text-foreground mb-4 flex items-center gap-2"
    >
      <LineChart className="h-6 w-6 text-destructive" />
      Headline numbers
    </h2>
    <p className="text-muted-foreground mb-8 max-w-3xl">
      Pulled from Tom Warren's reporting at The Verge (May 14, 2026), his
      earlier January 22, 2026 piece "Claude Code is suddenly everywhere inside
      Microsoft", Stratechery / Major Matters analysis of Copilot Cowork, and
      secondary coverage from ABP Live, Pomegra and byteiota.
    </p>

    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
      {headlineNumbers.map((n) => (
        <div
          key={n.label}
          className="bg-card border border-border rounded-xl p-5 hover:border-primary/30 transition-colors"
        >
          <div className={`p-2 rounded-lg bg-muted w-fit mb-3 ${n.color}`}>
            <n.icon className="h-5 w-5" />
          </div>
          <div className={`text-2xl font-bold mb-1 ${n.color}`}>{n.value}</div>
          <div className="font-semibold text-foreground mb-1">{n.label}</div>
          <p className="text-sm text-muted-foreground">{n.sub}</p>
        </div>
      ))}
    </div>
  </section>
);

const drivers = [
  {
    icon: DollarSign,
    title: "Per-token billing scaled faster than Microsoft modeled",
    body:
      "Sources told The Verge the cancellation is partly financial. Claude Code's pricing is dominated by token usage (Anthropic API + Claude Pro/Max tiers), so adoption inside a ~100K-engineer org translates directly into a variable monthly bill. Six months of 'very popular' usage was enough to force a course correction before fiscal year end.",
  },
  {
    icon: Bot,
    title: "Internal preference for Claude was undermining Copilot's story",
    body:
      "Microsoft sells GitHub Copilot to its enterprise customers as the flagship AI coding product. Having its own E+D engineers prefer Anthropic's Claude Code — and openly so, per The Verge's January 2026 piece — was both off-message and expensive. Convergence on Copilot CLI fixes the optics and the P&L.",
  },
  {
    icon: Building2,
    title: "Microsoft owns the infrastructure — but not the per-token margin",
    body:
      "Microsoft is a major Anthropic infrastructure partner via Azure, but per-token spend on Claude Code still pays Anthropic, not Microsoft. Routing those workloads through Copilot CLI keeps the gross margin inside the company, even when the underlying model capability gap is small.",
  },
  {
    icon: Wrench,
    title: "Microsoft's own resale bundle made the in-house cut easier",
    body:
      "In March 2026, Microsoft launched Copilot Cowork inside the new M365 E7 bundle at $30 per user per month — Anthropic's agentic technology, white-labeled (Stratechery / Major Matters). With a fixed-price Anthropic-powered SKU already on the market for customers, Microsoft did not need its own engineers running raw Claude Code seats.",
  },
  {
    icon: AlertTriangle,
    title: "It is part of a broader 2026 'AI spend hangover'",
    body:
      "The same week The Verge broke the Microsoft story, it also published 'Uber president says AI spending is getting harder to justify'. Two of the largest enterprise AI buyers on the planet flagged AI cost discipline in the same window — this is not an isolated Microsoft accounting choice.",
  },
];

const WhyDeclining = () => (
  <section className="py-10 sm:py-12">
    <h2
      id="why-declining"
      className="text-2xl sm:text-3xl font-bold text-foreground mb-4 flex items-center gap-2"
    >
      <Bot className="h-6 w-6 text-primary" />
      Why Microsoft is doing this — five drivers
    </h2>
    <p className="text-muted-foreground mb-8 max-w-3xl">
      No single reason explains the rollback. Microsoft's stated rationale is
      product convergence; The Verge's sources add a financial one. Both are
      almost certainly true at the same time.
    </p>

    <div className="grid gap-4 sm:gap-6">
      {drivers.map((d) => (
        <div
          key={d.title}
          className="bg-card border border-border rounded-xl p-5 sm:p-6 hover:border-primary/30 transition-colors"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-primary/10 text-primary shrink-0">
              <d.icon className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {d.title}
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base">{d.body}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const Sources = () => (
  <section className="py-8 sm:py-10">
    <h2
      id="sources"
      className="text-xl sm:text-2xl font-bold text-foreground mb-4"
    >
      Sources (all links verified)
    </h2>
    <ul className="list-disc list-inside text-sm sm:text-base text-muted-foreground space-y-2">
      <li>
        Tom Warren —{" "}
        <a
          href="https://www.theverge.com/tech/930447/microsoft-claude-code-discontinued-notepad"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          "Microsoft starts canceling Claude Code licenses"
        </a>
         , The Verge / Notepad, May 14, 2026 (primary source).
      </li>
      <li>
        Tom Warren —{" "}
        <a
          href="https://www.theverge.com/tech/865689/microsoft-claude-code-anthropic-partnership-notepad"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          "Claude Code is suddenly everywhere inside Microsoft"
        </a>
         , The Verge / Notepad, Jan 22, 2026.
      </li>
      <li>
        Annie Sharma —{" "}
        <a
          href="https://news.abplive.com/technology/why-microsoft-cancelled-claude-code-licenses-for-100000-engineers-token-pricing-cost-1846206"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          "Microsoft Pulled The Plug On 100,000 Claude Code Licenses Due To Rising Costs"
        </a>
         , ABP Live, May 26, 2026.
      </li>
      <li>
        Pomegra News —{" "}
        <a
          href="https://pomegra.io/news/microsoft-cancels-claude-code-licenses-in-2026-over-runaway-ai-costs"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          "Microsoft Cancels Claude Code Licenses in 2026 Over Runaway AI Costs"
        </a>
         .
      </li>
      <li>
        Beginners in AI —{" "}
        <a
          href="https://beginnersinai.org/microsoft-cancels-claude-code/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          "Microsoft Cancels Claude Code Licenses: The Real Reason (2026)"
        </a>
         .
      </li>
      <li>
        byteiota —{" "}
        <a
          href="https://byteiota.com/microsoft-claude-code-killed-copilot-cli-by-june-2026/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          "Microsoft Claude Code Killed: Copilot CLI by June 2026"
        </a>
         .
      </li>
      <li>
        Major Matters —{" "}
        <a
          href="https://majormatters.co/p/microsoft-claude-code-walkback-cowork-resale"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          "Microsoft Resells Anthropic's Tech at a 65% Premium. It Just Canceled the Free Version."
        </a>{" "}
        (Copilot Cowork / M365 E7 analysis).
      </li>
      <li>
        The Verge —{" "}
        <a
          href="https://www.theverge.com/transportation/937116/uber-ai-investment-hard-to-justify"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          "Uber president says AI spending is getting 'harder to justify'"
        </a>{" "}
        (industry context, same week).
      </li>
      <li>
        Microsoft Investor Relations —{" "}
        <a
          href="https://www.microsoft.com/en-us/investor"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          Microsoft fiscal calendar (FY ends June 30)
        </a>
         .
      </li>
    </ul>
  </section>
);

const MicrosoftDropsClaudeCode = () => {
  return (
    <>
      <InsightSEO
        slug="microsoft-drops-claude-code"
        additionalKeywords={[
          "Microsoft Claude Code",
          "Microsoft cancels Claude Code",
          "Claude Code licenses",
          "GitHub Copilot CLI",
          "Anthropic Microsoft",
          "enterprise AI cost",
          "token-based AI pricing",
          "Microsoft Experiences and Devices",
          "AI budget overrun 2026",
          "Copilot Cowork M365 E7",
        ]}
      />
      <InsightEngagementTracker
        slug="microsoft-drops-claude-code"
        title="Microsoft Drops Claude Code by June 30, 2026"
      />
      <InsightsNavbar showBack />
      <ScrollProgressIndicator />
      <TableOfContents />
      <InsightsLeadModal />
      <main className="page-container pb-16 sm:pb-20">
        <InsightsBreadcrumb
          category="AI Development"
          currentPage="Microsoft Drops Claude Code"
        />
        <HeroSection />

        <AnimatedSection delay={100} className="mt-10 sm:mt-12">
          <Takeaway />
        </AnimatedSection>

        <AnimatedSection delay={50} className="mt-8 sm:mt-10">
          <FounderQuote />
        </AnimatedSection>

        <div className="section-divider" />

        <AnimatedSection delay={0}>
          <HeadlineNumbers />
        </AnimatedSection>

        <AnimatedSection delay={0}>
          <WhyDeclining />
        </AnimatedSection>

        <AnimatedSection delay={0}>
          <InsightBenchmarks insightSlug="microsoft-drops-claude-code" />
        </AnimatedSection>

        <AnimatedSection delay={0}>
          <Sources />
        </AnimatedSection>

        <AnimatedSection delay={0}>
          <InsightFAQSection insightSlug="microsoft-drops-claude-code" />
        </AnimatedSection>

        <AnimatedSection delay={0}>
          <RelatedTools insightSlug="microsoft-drops-claude-code" />
        </AnimatedSection>
      </main>
      <Footer />
    </>
  );
};

export default MicrosoftDropsClaudeCode;